import { useCallback, useMemo } from 'react'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import styled from '@emotion/styled'

import { Label, Tooltip } from 'shared/ui'
import { Icon } from 'util/ui'

import ArrayOfStringsItem from './ArrayOfStringsItem'
import useArrayOfStringsState from './useArrayOfStringsState'

export default styled(ArrayOfStrings)`
  .RemoveButton {
    width: 43px;
  }

  .link {
    color: ${props => props.theme.color.primary.brand};
    cursor: pointer;
  }
`

function ArrayOfStrings({
  className,
  maxItems = Infinity,
  noun = 'item',
  name,
  numberedKeys,
  primary = false,
  primaryName,
  onChange,
  onCountChange,
  items,
  initialItems = [],
  secondaryNamePrefix,
  sortable = false,
  title,
}) {
  const {
    doAdd,
    doRemove,
    doReorderItems,
    doSetRecord,
    doSetPrimary,
    items: managedItems,
  } = useArrayOfStringsState({
    initialItems: items || initialItems,
    items,
    maxItems,
    onChange,
    onCountChange,
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )
  const currentItems = useMemo(() => items || managedItems || [], [
    items,
    managedItems,
  ])
  const itemIds = useMemo(() => currentItems.map((_, index) => `${index}`), [
    currentItems,
  ])
  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      doReorderItems({ oldIndex: Number(active.id), newIndex: Number(over.id) })
    },
    [doReorderItems]
  )

  let primaryRendered = false
  const content = (
    <div className={className}>
      <Label as="div">
        {`${title} `}
        {primary && (
          <Tooltip
            className="tooltipTrigger"
            as="span"
            position="right"
            tooltip={`The primary ${noun} is selected by clicking the radio button.`}
          >
            <Icon name="question circle outline" />
          </Tooltip>
        )}
      </Label>
      {currentItems.map(({ isPrimary, value }, index) => {
        if (isPrimary) primaryRendered = true
        const numberedName = isPrimary
          ? primaryName
          : `${secondaryNamePrefix}${primaryRendered ? index : index + 1}`
        const computedName = numberedKeys ? numberedName : `${name}[]`
        return (
          <ArrayOfStringsItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            doSetRecord={doSetRecord}
            doSetPrimary={doSetPrimary}
            doRemove={currentItems.length > 1 || value !== '' ? doRemove : null}
            index={index}
            primary={primary}
            isPrimary={isPrimary}
            primaryName={primaryName}
            name={computedName}
            noun={noun}
            secondaryNamePrefix={secondaryNamePrefix}
            sortable={sortable}
            value={value}
          />
        )
      })}
      {numberedKeys &&
        new Array(Math.min(maxItems - 1, maxItems - currentItems.length))
          .fill(null)
          .map((value, index) => {
            return (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                type="hidden"
                value=""
                name={`${secondaryNamePrefix}${currentItems.length +
                  index}.content`}
              />
            )
          })}
      {!items ||
        (items.length < maxItems && (
          <div className="link" onClick={doAdd}>
            + Add {noun}
          </div>
        ))}
    </div>
  )

  if (!sortable) return content

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {content}
      </SortableContext>
    </DndContext>
  )
}
